import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Box } from '@mui/material';
import {
  DataGrid,
  gridPaginatedVisibleSortedGridRowIdsSelector,
  useGridApiRef,
  type DataGridProps,
  type GridCallbackDetails,
  type GridColDef,
  type GridColumnGroupingModel,
  type GridDensity,
  type GridFilterModel,
  type GridPaginationModel,
  type GridRenderCellParams,
  type GridRowClassNameParams,
  type GridRowId,
  type GridRowParams,
  type GridRowSelectionModel,
  type GridRowsProp,
  type GridSortModel,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { composeSx } from '../../utils/composeSx';
import { toCellString } from '../../utils/format';
import { HF_HEADER_TITLE_CLASS, headerChromePx, useColumnSizing, type HFGridColDef } from './columnSizing';
import { DragGrip, useDragReorder } from './dragReorder';
import { ColumnSearchHeader, HFDataTableToolbar, filterRowsByFields, filterRowsByQuickQuery } from './search';
import { clampSelectionToPage } from './selection';
import { useServerSearch, type HFServerSearch } from './serverSearch';
import {
  expandColumnWidth,
  flattenRowTree,
  panelChildDepth,
  rowTreeColumn,
  rowTreeSx,
  sortRowsByModel,
  toggleColumnDepth,
  useRowTreeExpanded,
  type HFDataTableRowTree,
  type RowTreeMeta,
} from './rowTree';
import { HF_DATA_TABLE_SX, ROW_DRAG_COLUMN_PX, ROW_DRAG_FIELD, stickyLeftSx } from './styles';
import { SummaryFooter, withAutoAggregates, type HFSummaryModel } from './summary';
import { useRowsScrollEnd } from './useRowsScrollEnd';

export type HFDataTableSummary<R extends GridValidRowModel = GridValidRowModel> = {
  /**
   * Per-field aggregator overrides. Numeric columns not listed here are
   * summed automatically; other columns show nothing unless listed.
   */
  fields?: HFSummaryModel<R>;
  /** Default `all`. `selected` falls back to all rows when nothing is selected. */
  scope?: 'all' | 'selected';
  /** Label shown in the first column when that field has no aggregator. Default `Total`. */
  label?: string;
};

export type HFDataTableProps<R extends GridValidRowModel = GridValidRowModel> = Omit<DataGridProps<R>, 'pagination' | 'columns'> & {
  columns: Array<HFGridColDef<R>>;
  /** Fill the parent container height instead of sizing to content. */
  fill?: boolean;
  /**
   * When paginated, header “select all” only selects the current page.
   * Community DataGrid forces `checkboxSelectionVisibleOnly: false`, so we emulate it.
   * Default: true.
   */
  pageScopedSelection?: boolean;
  /** Global quick-filter toolbar above the grid. */
  search?: boolean | { placeholder?: string };
  /**
   * Run global + column search on the server. Omit for client-side search.
   * Summary aggregates only the rows the server returns.
   */
  serverSearch?: HFServerSearch<R>;
  /**
   * Magnifier on each filterable column header → search dialog.
   * Default: true.
   */
  columnSearch?: boolean;
  /**
   * Per-column search text. Pass with `onColumnQueriesChange` to own the filters
   * outside the table (they are not part of a saved layout).
   */
  columnQueries?: Record<string, string>;
  onColumnQueriesChange?: (queries: Record<string, string>) => void;
  /**
   * Drag-reorder columns (Community has no native reorder).
   * Default: true. Sticky is separate via `stickyFirstColumn`.
   */
  columnDrag?: boolean;
  /** Drag-reorder rows via a grip column. Default: false. */
  rowDrag?: boolean;
  /** Called after a column drag-reorder with the new field order. */
  onColumnsReorder?: (fields: string[]) => void;
  /** Called after a row drag-reorder with the new row list. */
  onRowsReorder?: (rows: R[]) => void;
  /** Freeze the leftmost data column (and checkbox column when present). */
  stickyFirstColumn?: boolean;
  /**
   * Keep every column mounted so a CSS-sticky column (for example actions on the right) is not
   * unmounted when it scrolls out of the viewport. Row virtualization stays on.
   */
  disableColumnVirtualization?: boolean;
  /**
   * When `stickyFirstColumn` is on, how many leading data columns to pin.
   * Default 1. Counts > 1 need a matching fixed `stickyColumnWidth`.
   */
  stickyColumnCount?: number;
  /** Fixed width of each pinned data column when `stickyColumnCount` > 1. */
  stickyColumnWidth?: number;
  /** Totals row rendered in the footer slot. */
  summary?: HFDataTableSummary<R>;
  /** Fired once when the user scrolls near the bottom (Community scroll-end polyfill). */
  onRowsScrollEnd?: () => void;
  /** Distance from bottom that triggers `onRowsScrollEnd`. Default 120. */
  scrollEndThresholdPx?: number;
  /**
   * Flattened parent/child rows. Sort applies to parents; children are not selectable.
   * Parent filtering is the quick filter and column search. `filterModel.items` are not applied.
   * The table stays in server filter mode so the grid cannot filter the flattened list and drop a child whose parent no longer matches.
   * With `renderChildRow`, child rows sit in nested panels. `panelFromDepth` keeps shallower children in `columns`.
   * Without `renderChildRow`, children reuse `columns`.
   * Pagination counts flattened rows, so a long expand is better with a scrolling grid than a short page.
   * Pass `sortModel` without `onSortModelChange` and the sort icons will not move the rows.
   */
  rowTree?: HFDataTableRowTree<R>;
};

function emptySelection(): GridRowSelectionModel {
  return { type: 'include', ids: new Set() };
}

export function HFDataTable<R extends GridValidRowModel = GridValidRowModel>({
  fill = false,
  pageScopedSelection = true,
  search,
  serverSearch,
  columnSearch = true,
  columnQueries: columnQueriesProp,
  onColumnQueriesChange,
  columnDrag = true,
  rowDrag = false,
  onColumnsReorder,
  onRowsReorder,
  stickyFirstColumn = false,
  disableColumnVirtualization = false,
  stickyColumnCount = 1,
  stickyColumnWidth,
  summary,
  onRowsScrollEnd,
  scrollEndThresholdPx = 120,
  density = 'compact',
  rowHeight = 36,
  columnHeaderHeight = 40,
  disableRowSelectionOnClick: disableRowSelectionOnClickProp,
  autosizeOnMount = true,
  autosizeOptions,
  showCellVerticalBorder = false,
  showColumnVerticalBorder = false,
  checkboxSelection = false,
  rowSelectionModel: controlledSelection,
  onRowSelectionModelChange,
  apiRef: externalApiRef,
  sx,
  autoHeight,
  slots,
  slotProps,
  rows,
  columns: columnsProp,
  columnBufferPx,
  showToolbar,
  disableColumnMenu,
  loading: loadingProp,
  filterMode: filterModeProp,
  onFilterModelChange: onFilterModelChangeProp,
  getRowId,
  getRowClassName,
  isRowSelectable,
  sortModel: sortModelProp,
  onSortModelChange,
  sortingMode,
  disableColumnFilter,
  initialState,
  getRowHeight,
  rowTree,
  ...props
}: HFDataTableProps<R>) {
  const internalApiRef = useGridApiRef();
  const apiRef = externalApiRef ?? internalApiRef;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const treeMetaRef = useRef<Map<GridRowId, { depth: number }>>(new Map());
  const [uncontrolledSelection, setUncontrolledSelection] = useState<GridRowSelectionModel>(emptySelection);
  const [uncontrolledColumnQueries, setUncontrolledColumnQueries] = useState<Record<string, string>>({});
  const columnQueries = columnQueriesProp ?? uncontrolledColumnQueries;
  const [quickQuery, setQuickQuery] = useState('');
  // Mirror of useServerSearch rows so useDragReorder can seed from server results.
  const [serverRows, setServerRows] = useState<R[] | null>(null);

  const sourceRows = useMemo(() => {
    const base = serverSearch ? (serverRows ?? rows) : rows;
    return (base ?? []) as R[];
  }, [serverSearch, serverRows, rows]);

  const { columnOrder, localRows, dragging, startDrag, dragSourceSx, ghost } = useDragReorder<R>({
    containerRef,
    columns: columnsProp,
    rows: sourceRows,
    onColumnsReorder,
    onRowsReorder,
  });

  const { rows: fetchedServerRows, loading: serverLoading } = useServerSearch<R>({
    serverSearch,
    query: quickQuery,
    columnQueries,
    paused: dragging,
  });

  if (fetchedServerRows !== serverRows) {
    setServerRows(fetchedServerRows);
  }

  const selection = controlledSelection ?? uncontrolledSelection;
  const isSelectionControlled = controlledSelection !== undefined;

  const resolveRowId = useCallback(
    (row: R): GridRowId => {
      if (getRowId) return getRowId(row);
      return (row as { id?: GridRowId }).id ?? '';
    },
    [getRowId],
  );
  const { expandedIds, toggle: toggleExpanded } = useRowTreeExpanded(rowTree);
  /** Expanding inserts rows; DataGrid would otherwise jump the viewport to the top. */
  const toggleExpandedPreservingScroll = useCallback(
    (id: GridRowId) => {
      const api = apiRef.current;
      const top = api?.getScrollPosition?.().top ?? 0;
      toggleExpanded(id);
      requestAnimationFrame(() => {
        apiRef.current?.scroll?.({ top });
      });
    },
    [apiRef, toggleExpanded],
  );
  const [uncontrolledSort, setUncontrolledSort] = useState<GridSortModel>(initialState?.sorting?.sortModel ?? []);
  const sortModel = sortModelProp ?? uncontrolledSort;
  const handleSortModelChange = useCallback(
    (model: GridSortModel, details: GridCallbackDetails) => {
      if (sortModelProp === undefined) setUncontrolledSort(model);
      onSortModelChange?.(model, details);
    },
    [onSortModelChange, sortModelProp],
  );

  // Checkbox multi-select expects row clicks to toggle the row too.
  const disableRowSelectionOnClick = disableRowSelectionOnClickProp ?? !checkboxSelection;

  useRowsScrollEnd(apiRef, onRowsScrollEnd, scrollEndThresholdPx);

  const handleSelectionChange = useCallback(
    (model: GridRowSelectionModel, details: GridCallbackDetails) => {
      let next = model;

      if (pageScopedSelection && checkboxSelection) {
        try {
          let pageIds = gridPaginatedVisibleSortedGridRowIdsSelector(apiRef);
          if (rowTree) pageIds = pageIds.filter((id) => (treeMetaRef.current.get(id)?.depth ?? 0) === 0);
          next = clampSelectionToPage(model, selection, pageIds);
        } catch {
          next = model;
        }
      }

      if (!isSelectionControlled) setUncontrolledSelection(next);
      onRowSelectionModelChange?.(next, details);
    },
    [apiRef, checkboxSelection, isSelectionControlled, onRowSelectionModelChange, pageScopedSelection, rowTree, selection],
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel, details: GridCallbackDetails<'filter'>) => {
      setQuickQuery((model.quickFilterValues ?? []).join(' '));
      onFilterModelChangeProp?.(model, details);
    },
    [onFilterModelChangeProp],
  );

  const setColumnQuery = useCallback(
    (field: string, query: string) => {
      const apply = (prev: Record<string, string>) => {
        const next = { ...prev };
        if (!query.trim()) delete next[field];
        else next[field] = query;
        return next;
      };
      if (columnQueriesProp !== undefined) {
        onColumnQueriesChange?.(apply(columnQueriesProp));
        return;
      }
      setUncontrolledColumnQueries(apply);
    },
    [columnQueriesProp, onColumnQueriesChange],
  );

  const orderedColumns = useMemo(() => {
    // Without drag-reorder the parent owns the order, and generated column sets
    // (pivot expand/collapse) must not be pinned to the previously seen order.
    if (!columnDrag) return columnsProp;
    const byField = new Map(columnsProp.map((c) => [c.field, c]));
    return columnOrder.map((field) => byField.get(field)).filter(Boolean) as Array<HFGridColDef<R>>;
  }, [columnDrag, columnsProp, columnOrder]);

  const parentRows = useMemo(() => {
    const list = rowDrag ? localRows : sourceRows;
    if (serverSearch) return list;
    return filterRowsByQuickQuery(filterRowsByFields(list, columnQueries), quickQuery);
  }, [sourceRows, localRows, rowDrag, columnQueries, quickQuery, serverSearch]);

  const sortedParents = useMemo(() => {
    if (!rowTree || sortingMode === 'server') return parentRows;
    return sortRowsByModel(parentRows, sortModel, orderedColumns, apiRef, resolveRowId);
  }, [rowTree, sortingMode, parentRows, sortModel, orderedColumns, apiRef, resolveRowId]);

  const flattened = useMemo(() => {
    if (!rowTree) return { rows: sortedParents, meta: new Map<GridRowId, RowTreeMeta>() };
    return flattenRowTree(sortedParents, rowTree.getChildren, resolveRowId, expandedIds);
  }, [rowTree, sortedParents, resolveRowId, expandedIds]);
  const treeMeta = flattened.meta;
  treeMetaRef.current = treeMeta;
  const visibleRows = flattened.rows;

  const treeSource = rowDrag ? localRows : sourceRows;
  const panelFrom = panelChildDepth(rowTree);
  const fullPanel = panelFrom === 1;
  const sameColumnIndent =
    rowTree && !fullPanel ? toggleColumnDepth(treeSource, rowTree.getChildren, panelFrom == null ? Infinity : panelFrom - 1) : 0;
  const expandWidth = rowTree ? expandColumnWidth(fullPanel, sameColumnIndent) : 0;

  const isChildRow = useCallback((id: GridRowId) => (treeMeta.get(id)?.depth ?? 0) > 0, [treeMeta]);

  const sizedColumns = useColumnSizing({
    apiRef,
    containerRef,
    columns: orderedColumns,
    enabled: autosizeOnMount,
    paused: dragging || serverLoading,
    chromePx: headerChromePx({ columnDrag, columnSearch, disableColumnMenu: Boolean(disableColumnMenu) }),
    autosizeOptions,
    rows: parentRows,
  });

  // Sticky cells must stay mounted. Column virtualization unmounts them mid-scroll.
  // DataGrid autosize/resize re-enables column virtualization in a `finally` block —
  // re-assert after sizedColumns settles and on every columns/width change (microtask
  // so we run after that finally).
  useEffect(() => {
    if (!stickyFirstColumn && !disableColumnVirtualization) return;
    const api = apiRef.current;
    if (!api) return;

    const keepColumnsMounted = () => {
      queueMicrotask(() => api.unstable_setColumnVirtualization(false));
    };
    keepColumnsMounted();

    const unsubs = [api.subscribeEvent('columnsChange', keepColumnsMounted), api.subscribeEvent('columnWidthChange', keepColumnsMounted)];
    return () => unsubs.forEach((unsubscribe) => unsubscribe());
  }, [apiRef, disableColumnVirtualization, stickyFirstColumn, sizedColumns]);

  const resolvedColumns = useMemo(() => {
    const enhanced = sizedColumns.map((col) => {
      const filterable = col.filterable !== false && col.type !== 'actions';
      const showSearch = columnSearch && filterable;
      const showDrag = columnDrag && col.field !== ROW_DRAG_FIELD;

      // Headers stay left-aligned even when cell values are right-aligned.
      const withHeaderAlign: GridColDef<R> = {
        ...col,
        headerAlign: col.field === ROW_DRAG_FIELD ? col.headerAlign : 'left',
      };

      if (!showSearch && !showDrag) return withHeaderAlign;

      const prevHeader = withHeaderAlign.renderHeader;
      return {
        ...withHeaderAlign,
        renderHeader: (params) => {
          const start: ReactNode = showDrag ? (
            <DragGrip
              label={`Reorder ${params.colDef.headerName ?? params.field}`}
              iconSize={14}
              onGripPointerDown={(event) => startDrag(event, 'column', params.field, params.colDef.headerName ?? params.field)}
            />
          ) : null;

          if (showSearch) {
            return <ColumnSearchHeader params={params} value={columnQueries[params.field] ?? ''} onChange={setColumnQuery} startAdornment={start} />;
          }

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              {start}
              {prevHeader ? (
                prevHeader(params)
              ) : (
                <Box
                  component="span"
                  className={HF_HEADER_TITLE_CLASS}
                  sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {params.colDef.headerName ?? params.field}
                </Box>
              )}
            </Box>
          );
        },
      } satisfies GridColDef<R>;
    });

    // Ghost label: first visible column's formatted value gives a recognizable row preview.
    const firstField = sizedColumns[0]?.field;
    const rowLabel = (params: GridRenderCellParams<R>): string => {
      if (firstField == null) return String(params.id);
      const cell = params.api.getCellParams(params.id, firstField);
      const label = toCellString(cell.formattedValue ?? cell.value);
      return label === '' ? String(params.id) : label;
    };

    const dragCol: GridColDef<R> = {
      field: ROW_DRAG_FIELD,
      headerName: '',
      width: ROW_DRAG_COLUMN_PX,
      minWidth: ROW_DRAG_COLUMN_PX,
      maxWidth: ROW_DRAG_COLUMN_PX,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      resizable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<R>) => {
        if ((treeMeta.get(params.id)?.depth ?? 0) > 0) return null;
        return (
          <DragGrip label="Drag row" iconSize={16} onGripPointerDown={(event) => startDrag(event, 'row', String(params.id), rowLabel(params))} />
        );
      },
    };

    const expandCol =
      rowTree == null
        ? null
        : rowTreeColumn<R>({
            meta: treeMeta,
            expandedIds,
            toggle: toggleExpandedPreservingScroll,
            tree: rowTree,
            spanCount: enhanced.length + 1,
            width: expandWidth,
            getId: resolveRowId,
          });

    return [rowDrag ? dragCol : null, expandCol, ...enhanced].filter((column): column is GridColDef<R> => column != null);
  }, [
    sizedColumns,
    columnSearch,
    columnDrag,
    rowDrag,
    columnQueries,
    setColumnQuery,
    startDrag,
    rowTree,
    treeMeta,
    expandedIds,
    toggleExpandedPreservingScroll,
    expandWidth,
    resolveRowId,
  ]);

  const resolvedAutoHeight = autoHeight ?? !fill;
  const heightSx = useMemo(() => {
    if (fill) return { width: '100%', height: '100%', minHeight: 0 };
    if (resolvedAutoHeight) return { width: '100%' };
    return { width: '100%', height: 360 };
  }, [fill, resolvedAutoHeight]);

  const searchEnabled = Boolean(search);
  const searchPlaceholder = typeof search === 'object' ? search.placeholder : undefined;

  const mergedSlots = useMemo(() => {
    const next = { ...slots };
    if (searchEnabled) {
      next.toolbar = (slots?.toolbar ?? HFDataTableToolbar) as NonNullable<typeof slots>['toolbar'];
    }
    if (summary) {
      next.footer =
        slots?.footer ??
        (() => (
          <SummaryFooter<R>
            rows={visibleRows}
            columns={resolvedColumns}
            fields={withAutoAggregates(resolvedColumns, summary.fields)}
            scope={summary.scope}
            label={summary.label}
            selection={checkboxSelection ? selection : undefined}
            isChildRow={rowTree ? isChildRow : undefined}
          />
        ));
    }
    return next;
  }, [slots, searchEnabled, summary, visibleRows, resolvedColumns, checkboxSelection, selection, rowTree, isChildRow]);

  const mergedSlotProps = useMemo(() => {
    if (!searchEnabled) return slotProps;
    return {
      ...slotProps,
      toolbar: {
        ...(slotProps?.toolbar as object | undefined),
        ...(searchPlaceholder ? { placeholder: searchPlaceholder } : {}),
        ...(serverSearch ? { debounceMs: 0 } : {}),
        onQueryChange: setQuickQuery,
      },
    };
  }, [slotProps, searchEnabled, searchPlaceholder, serverSearch]);

  const resolveRowClassName = useCallback(
    (params: GridRowClassNameParams<R>) => {
      const base = getRowClassName?.(params) ?? '';
      if (!rowTree) return base;
      const meta = treeMeta.get(params.id);
      const extra: string[] = [];
      if (meta && meta.depth > 0) extra.push('hf-row-child', `hf-row-depth-${meta.depth}`);
      if (meta?.hasChildren && expandedIds.has(params.id)) extra.push('hf-row-expanded');
      return [base, ...extra].filter(Boolean).join(' ');
    },
    [expandedIds, getRowClassName, rowTree, treeMeta],
  );

  const resolveRowSelectable = useCallback(
    (params: GridRowParams<R>) => {
      if ((treeMeta.get(params.id)?.depth ?? 0) > 0) return false;
      return isRowSelectable ? isRowSelectable(params) : true;
    },
    [isRowSelectable, treeMeta],
  );

  const resolveRowHeight = useCallback(
    (params: Parameters<NonNullable<typeof getRowHeight>>[0]) => {
      const base = getRowHeight?.(params) ?? rowHeight;
      if (!rowTree?.renderChildRow || typeof base !== 'number') return base;
      return base + (treeMeta.get(params.id)?.closesPanels ?? 0) * 8;
    },
    [getRowHeight, rowHeight, rowTree, treeMeta],
  );

  const firstDataField = sizedColumns.find((column) => column.field !== ROW_DRAG_FIELD)?.field;

  return (
    <Box ref={containerRef} sx={heightSx}>
      <DataGrid<R>
        apiRef={apiRef}
        density={density}
        rowHeight={rowHeight}
        columnHeaderHeight={columnHeaderHeight}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        autosizeOnMount={false}
        showCellVerticalBorder={showCellVerticalBorder}
        showColumnVerticalBorder={showColumnVerticalBorder}
        checkboxSelection={checkboxSelection}
        autoHeight={resolvedAutoHeight}
        rowSelectionModel={checkboxSelection || !disableRowSelectionOnClick || isSelectionControlled ? selection : undefined}
        onRowSelectionModelChange={
          checkboxSelection || !disableRowSelectionOnClick || isSelectionControlled || onRowSelectionModelChange ? handleSelectionChange : undefined
        }
        rows={visibleRows}
        columns={resolvedColumns}
        showToolbar={showToolbar ?? searchEnabled}
        slots={mergedSlots}
        slotProps={mergedSlotProps}
        columnBufferPx={columnBufferPx}
        disableColumnMenu={disableColumnMenu}
        disableColumnFilter={rowTree ? (disableColumnFilter ?? true) : disableColumnFilter}
        filterMode={rowTree ? 'server' : (filterModeProp ?? (serverSearch ? 'server' : undefined))}
        sortingMode={rowTree ? 'server' : sortingMode}
        sortModel={rowTree ? sortModel : sortModelProp}
        onSortModelChange={rowTree ? handleSortModelChange : onSortModelChange}
        getRowId={getRowId}
        getRowClassName={rowTree ? resolveRowClassName : getRowClassName}
        isRowSelectable={rowTree ? resolveRowSelectable : isRowSelectable}
        getRowHeight={rowTree ? resolveRowHeight : getRowHeight}
        initialState={initialState}
        onFilterModelChange={handleFilterModelChange}
        loading={Boolean(loadingProp) || serverLoading}
        sx={composeSx(
          HF_DATA_TABLE_SX,
          stickyFirstColumn
            ? stickyLeftSx({
                checkbox: checkboxSelection,
                rowDrag,
                dataColumnCount: stickyColumnCount,
                dataColumnWidth: stickyColumnWidth,
                expandWidth: rowTree ? expandWidth : undefined,
              })
            : undefined,
          rowTree ? rowTreeSx(firstDataField, fullPanel ? 0 : sameColumnIndent + 1) : undefined,
          fill ? { height: '100%' } : undefined,
          dragSourceSx,
          sx,
        )}
        {...props}
      />
      {ghost}
    </Box>
  );
}

export type {
  GridCallbackDetails,
  GridColDef,
  GridColumnGroupingModel,
  GridDensity,
  GridFilterModel,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridRowsProp,
  GridSortModel,
  GridValidRowModel,
};

export type { HFColumnSize, HFGridColDef } from './columnSizing';
export { headerChromePx } from './columnSizing';
export type { HFSummaryKind, HFSummaryModel, HFSummaryAggregator } from './summary';
export { computeSummary, withAutoAggregates } from './summary';
export type { HFDataTableRowTree, HFDataTableRowTreeContext } from './rowTree';
export { filterRowsByFields, filterRowsByDateRange, filterRowsByQuickQuery } from './search';
export type { HFServerSearch, HFServerSearchRequest } from './serverSearch';
export * from './columnTypes';
